import React, { useState, useRef, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import './PaceCalculator.css';
import WorldRecords from './WorldRecords';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const formatPace = (pace, useKilometers) => {
  if (!pace) return '-';
  
  const paceMatch = pace.match(/(\d+):(\d+)/);
  if (!paceMatch) return '-';
  
  const minutes = parseInt(paceMatch[1]);
  const seconds = parseInt(paceMatch[2]);
  let paceInSeconds = minutes * 60 + seconds;
  
  if (!useKilometers) {
    paceInSeconds = Math.round(paceInSeconds * 1.60934);
  }
  
  const newMinutes = Math.floor(paceInSeconds / 60);
  const newSeconds = paceInSeconds % 60;
  
  return `${newMinutes}:${newSeconds.toString().padStart(2, '0')} (min/${useKilometers ? 'km' : 'mile'})`;
};

const PaceCalculator = () => {
  const [distance, setDistance] = useState('');
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [seconds, setSeconds] = useState('');
  const [pace, setPace] = useState(null);
  const [useKilometers, setUseKilometers] = useState(false);
  const [isPaceMode, setIsPaceMode] = useState(true);
  const [paceMinutes, setPaceMinutes] = useState('');
  const [paceSeconds, setPaceSeconds] = useState('00');
  const [totalTime, setTotalTime] = useState(null);
  const [strategyMode, setStrategyMode] = useState('none'); // 'none', 'halves', 'quarters'
  const [strategyPaces, setStrategyPaces] = useState({
    firstHalf: { minutes: '', seconds: '' },
    secondHalf: { minutes: '', seconds: '' },
    firstQuarter: { minutes: '', seconds: '' },
    secondQuarter: { minutes: '', seconds: '' },
    thirdQuarter: { minutes: '', seconds: '' },
    fourthQuarter: { minutes: '', seconds: '' }
  });

  const [chartData, setChartData] = useState(null);

  const [selectedDistance, setSelectedDistance] = useState('custom');
  const [customDistance, setCustomDistance] = useState('');

  const raceDistances = [
    { value: '1.5', label: '1.5km' },
    { value: '3', label: '3km' },
    { value: '5', label: '5km' },
    { value: '10', label: '10km' },
    { value: '15', label: '15km' },
    { value: '21.0975', label: 'Half Marathon' },
    { value: '42.195', label: 'Marathon' },
    { value: '4.828', label: '3 Miles' },
    { value: '8.047', label: '5 Miles' },
    { value: '16.093', label: '10 Miles' },
    { value: 'custom', label: 'Custom Distance' }
  ];

  const handleDistanceChange = (e) => {
    const value = e.target.value;
    setSelectedDistance(value);
    if (value !== 'custom') {
      setDistance(value);
      setCustomDistance('');
    }
  };

  const handleCustomDistanceChange = (e) => {
    const value = e.target.value;
    setCustomDistance(value);
    setDistance(value);
  };

  const calculatePace = () => {
    if (!distance || (!hours && !minutes && !seconds)) {
      setPace(null);
      return;
    }

    const totalMinutes = (parseInt(hours || 0) * 60) + 
                         parseInt(minutes || 0) + 
                         (parseInt(seconds || 0) / 60);
    
    if (totalMinutes === 0) {
      setPace(null);
      return;
    }

    const paceInMinutes = totalMinutes / parseFloat(distance);
    const paceMinutes = Math.floor(paceInMinutes);
    const paceSeconds = Math.round((paceInMinutes - paceMinutes) * 60);
    
    const unit = useKilometers ? 'min/km' : 'min/mile';
    setPace(`${paceMinutes}:${paceSeconds.toString().padStart(2, '0')} ${unit}`);
  };

  const calculateTotalTime = () => {
    if (!distance || !paceMinutes || !paceSeconds) {
      setTotalTime(null);
      return;
    }

    const paceInMinutes = parseInt(paceMinutes) + (parseInt(paceSeconds) / 60);
    const totalMinutes = paceInMinutes * parseFloat(distance);
    
    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = Math.floor(totalMinutes % 60);
    const remainingSeconds = Math.round((totalMinutes % 1) * 60);
    
    setTotalTime(`${totalHours}:${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`);
  };

  const calculateStrategyTime = () => {
    if (!distance) return null;

    let totalMinutes = 0;
    const unitDistance = parseFloat(distance);

    if (strategyMode === 'halves') {
      const firstHalfPace = parseInt(strategyPaces.firstHalf.minutes || 0) + 
                           (parseInt(strategyPaces.firstHalf.seconds || 0) / 60);
      const secondHalfPace = parseInt(strategyPaces.secondHalf.minutes || 0) + 
                            (parseInt(strategyPaces.secondHalf.seconds || 0) / 60);
      totalMinutes = (firstHalfPace * unitDistance / 2) + (secondHalfPace * unitDistance / 2);
    } else if (strategyMode === 'quarters') {
      const quarterDistance = unitDistance / 4;
      const firstQuarterPace = parseInt(strategyPaces.firstQuarter.minutes || 0) + 
                              (parseInt(strategyPaces.firstQuarter.seconds || 0) / 60);
      const secondQuarterPace = parseInt(strategyPaces.secondQuarter.minutes || 0) + 
                               (parseInt(strategyPaces.secondQuarter.seconds || 0) / 60);
      const thirdQuarterPace = parseInt(strategyPaces.thirdQuarter.minutes || 0) + 
                              (parseInt(strategyPaces.thirdQuarter.seconds || 0) / 60);
      const fourthQuarterPace = parseInt(strategyPaces.fourthQuarter.minutes || 0) + 
                               (parseInt(strategyPaces.fourthQuarter.seconds || 0) / 60);
      
      totalMinutes = (firstQuarterPace * quarterDistance) +
                    (secondQuarterPace * quarterDistance) +
                    (thirdQuarterPace * quarterDistance) +
                    (fourthQuarterPace * quarterDistance);
    }

    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = Math.floor(totalMinutes % 60);
    const remainingSeconds = Math.round((totalMinutes % 1) * 60);
    
    // Calculate average pace
    const averagePaceInMinutes = totalMinutes / unitDistance;
    const averagePaceMinutes = Math.floor(averagePaceInMinutes);
    const averagePaceSeconds = Math.round((averagePaceInMinutes - averagePaceMinutes) * 60);
    
    return {
      time: `${totalHours}:${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`,
      averagePace: `${averagePaceMinutes}:${averagePaceSeconds.toString().padStart(2, '0')}`
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isPaceMode) {
      calculatePace();
    } else if (strategyMode !== 'none') {
      setTotalTime(calculateStrategyTime().time);
    } else {
      calculateTotalTime();
    }
  };

  const toggleUnit = () => {
    setUseKilometers(!useKilometers);
    setPace(null);
    setTotalTime(null);
  };

  const toggleMode = () => {
    setIsPaceMode(!isPaceMode);
    setPace(null);
    setTotalTime(null);
  };

  const handleStrategyPaceChange = (section, field, value) => {
    setStrategyPaces(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const renderStrategyInputs = () => {
    if (strategyMode === 'halves') {
      return (
        <div className="strategy-inputs">
          <div className="strategy-section">
            <h4>Split in Halves</h4>
            <div className="pace-inputs">
              <div className="input-row">
                <div className="quarter-label">First Half</div>
                <div className="input-group">
                  <label>Minutes:</label>
                  <input
                    type="number"
                    value={strategyPaces.firstHalf.minutes}
                    onChange={(e) => handleStrategyPaceChange('firstHalf', 'minutes', e.target.value)}
                    min="0"
                  />
                </div>
                <div className="input-group">
                  <label>Seconds:</label>
                  <input
                    type="number"
                    value={strategyPaces.firstHalf.seconds}
                    onChange={(e) => handleStrategyPaceChange('firstHalf', 'seconds', e.target.value)}
                    min="0"
                    max="59"
                  />
                </div>
                <div className="pace-unit">
                  {useKilometers ? 'min/km' : 'min/mile'}
                </div>
              </div>
              <div className="input-row">
                <div className="quarter-label">Second Half</div>
                <div className="input-group">
                  <input
                    type="number"
                    value={strategyPaces.secondHalf.minutes}
                    onChange={(e) => handleStrategyPaceChange('secondHalf', 'minutes', e.target.value)}
                    min="0"
                  />
                </div>
                <div className="input-group">
                  <input
                    type="number"
                    value={strategyPaces.secondHalf.seconds}
                    onChange={(e) => handleStrategyPaceChange('secondHalf', 'seconds', e.target.value)}
                    min="0"
                    max="59"
                  />
                </div>
                <div className="pace-unit">
                  {useKilometers ? 'min/km' : 'min/mile'}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (strategyMode === 'quarters') {
      return (
        <div className="strategy-inputs">
          <div className="strategy-section">
            <h4>Split in Quarters</h4>
            <div className="pace-inputs">
              <div className="input-row">
                <div className="quarter-label">First Quarter</div>
                <div className="input-group">
                  <label>Minutes:</label>
                  <input
                    type="number"
                    value={strategyPaces.firstQuarter.minutes}
                    onChange={(e) => handleStrategyPaceChange('firstQuarter', 'minutes', e.target.value)}
                    min="0"
                  />
                </div>
                <div className="input-group">
                  <label>Seconds:</label>
                  <input
                    type="number"
                    value={strategyPaces.firstQuarter.seconds}
                    onChange={(e) => handleStrategyPaceChange('firstQuarter', 'seconds', e.target.value)}
                    min="0"
                    max="59"
                  />
                </div>
                <div className="pace-unit">
                  {useKilometers ? 'min/km' : 'min/mile'}
                </div>
              </div>
              <div className="input-row">
                <div className="quarter-label">Second Quarter</div>
                <div className="input-group">
                  <input
                    type="number"
                    value={strategyPaces.secondQuarter.minutes}
                    onChange={(e) => handleStrategyPaceChange('secondQuarter', 'minutes', e.target.value)}
                    min="0"
                  />
                </div>
                <div className="input-group">
                  <input
                    type="number"
                    value={strategyPaces.secondQuarter.seconds}
                    onChange={(e) => handleStrategyPaceChange('secondQuarter', 'seconds', e.target.value)}
                    min="0"
                    max="59"
                  />
                </div>
                <div className="pace-unit">
                  {useKilometers ? 'min/km' : 'min/mile'}
                </div>
              </div>
              <div className="input-row">
                <div className="quarter-label">Third Quarter</div>
                <div className="input-group">
                  <input
                    type="number"
                    value={strategyPaces.thirdQuarter.minutes}
                    onChange={(e) => handleStrategyPaceChange('thirdQuarter', 'minutes', e.target.value)}
                    min="0"
                  />
                </div>
                <div className="input-group">
                  <input
                    type="number"
                    value={strategyPaces.thirdQuarter.seconds}
                    onChange={(e) => handleStrategyPaceChange('thirdQuarter', 'seconds', e.target.value)}
                    min="0"
                    max="59"
                  />
                </div>
                <div className="pace-unit">
                  {useKilometers ? 'min/km' : 'min/mile'}
                </div>
              </div>
              <div className="input-row">
                <div className="quarter-label">Fourth Quarter</div>
                <div className="input-group">
                  <input
                    type="number"
                    value={strategyPaces.fourthQuarter.minutes}
                    onChange={(e) => handleStrategyPaceChange('fourthQuarter', 'minutes', e.target.value)}
                    min="0"
                  />
                </div>
                <div className="input-group">
                  <input
                    type="number"
                    value={strategyPaces.fourthQuarter.seconds}
                    onChange={(e) => handleStrategyPaceChange('fourthQuarter', 'seconds', e.target.value)}
                    min="0"
                    max="59"
                  />
                </div>
                <div className="pace-unit">
                  {useKilometers ? 'min/km' : 'min/mile'}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const generateChartData = () => {
    if (!distance) return null;

    const labels = [];
    const data = [];
    const backgroundColors = [];

    if (strategyMode === 'none') {
      // Single pace strategy
      const paceInMinutes = parseInt(paceMinutes || 0) + (parseInt(paceSeconds || 0) / 60);
      if (paceInMinutes > 0) {
        labels.push('Start', 'Finish');
        data.push(paceInMinutes, paceInMinutes);
        backgroundColors.push('#B100FF', '#B100FF');
      }
    } else if (strategyMode === 'halves') {
      const firstHalfPace = parseInt(strategyPaces.firstHalf.minutes || 0) + 
                           (parseInt(strategyPaces.firstHalf.seconds || 0) / 60);
      const secondHalfPace = parseInt(strategyPaces.secondHalf.minutes || 0) + 
                            (parseInt(strategyPaces.secondHalf.seconds || 0) / 60);
      
      const halfDistance = (parseFloat(distance) / 2).toFixed(2);
      const unit = useKilometers ? 'km' : 'mi';
      
      labels.push('Start', `${halfDistance}${unit}`, 'Finish');
      data.push(firstHalfPace, firstHalfPace, secondHalfPace);
      backgroundColors.push('#B100FF', '#B100FF', '#FF9800');
    } else if (strategyMode === 'quarters') {
      const firstQuarterPace = parseInt(strategyPaces.firstQuarter.minutes || 0) + 
                              (parseInt(strategyPaces.firstQuarter.seconds || 0) / 60);
      const secondQuarterPace = parseInt(strategyPaces.secondQuarter.minutes || 0) + 
                               (parseInt(strategyPaces.secondQuarter.seconds || 0) / 60);
      const thirdQuarterPace = parseInt(strategyPaces.thirdQuarter.minutes || 0) + 
                              (parseInt(strategyPaces.thirdQuarter.seconds || 0) / 60);
      const fourthQuarterPace = parseInt(strategyPaces.fourthQuarter.minutes || 0) + 
                               (parseInt(strategyPaces.fourthQuarter.seconds || 0) / 60);
      
      const quarterDistance = (parseFloat(distance) / 4).toFixed(2);
      const halfDistance = (parseFloat(distance) / 2).toFixed(2);
      const threeQuarterDistance = (parseFloat(distance) * 3 / 4).toFixed(2);
      const unit = useKilometers ? 'km' : 'mi';
      
      labels.push('Start', `${quarterDistance}${unit}`, `${halfDistance}${unit}`, `${threeQuarterDistance}${unit}`, 'Finish');
      data.push(firstQuarterPace, firstQuarterPace, secondQuarterPace, thirdQuarterPace, fourthQuarterPace);
      backgroundColors.push('#B100FF', '#B100FF', '#FF9800', '#FF5722', '#F44336');
    }

    if (data.length === 0) return null;

    return {
      labels,
      datasets: [
        {
          label: useKilometers ? 'Pace (min/km)' : 'Pace (min/mile)',
          data,
          borderColor: '#B100FF',
          backgroundColor: backgroundColors,
          tension: 0.1,
          pointRadius: 6,
          pointHoverRadius: 8,
        }
      ]
    };
  };

  const calculateSectorTimes = () => {
    if (!distance || strategyMode === 'none') return null;

    const unitDistance = parseFloat(distance);
    const sectors = [];

    if (strategyMode === 'halves') {
      const firstHalfPace = parseInt(strategyPaces.firstHalf.minutes || 0) + 
                           (parseInt(strategyPaces.firstHalf.seconds || 0) / 60);
      const secondHalfPace = parseInt(strategyPaces.secondHalf.minutes || 0) + 
                            (parseInt(strategyPaces.secondHalf.seconds || 0) / 60);
      
      const firstHalfTime = firstHalfPace * (unitDistance / 2);
      const secondHalfTime = secondHalfPace * (unitDistance / 2);
      
      sectors.push(
        { name: 'First Half', distance: `${unitDistance / 2} ${useKilometers ? 'km' : 'miles'}`, time: formatTime(firstHalfTime) },
        { name: 'Second Half', distance: `${unitDistance / 2} ${useKilometers ? 'km' : 'miles'}`, time: formatTime(secondHalfTime) }
      );
    } else if (strategyMode === 'quarters') {
      const quarterDistance = unitDistance / 4;
      const firstQuarterPace = parseInt(strategyPaces.firstQuarter.minutes || 0) + 
                              (parseInt(strategyPaces.firstQuarter.seconds || 0) / 60);
      const secondQuarterPace = parseInt(strategyPaces.secondQuarter.minutes || 0) + 
                               (parseInt(strategyPaces.secondQuarter.seconds || 0) / 60);
      const thirdQuarterPace = parseInt(strategyPaces.thirdQuarter.minutes || 0) + 
                              (parseInt(strategyPaces.thirdQuarter.seconds || 0) / 60);
      const fourthQuarterPace = parseInt(strategyPaces.fourthQuarter.minutes || 0) + 
                               (parseInt(strategyPaces.fourthQuarter.seconds || 0) / 60);
      
      sectors.push(
        { name: 'First Quarter', distance: `${quarterDistance} ${useKilometers ? 'km' : 'miles'}`, time: formatTime(firstQuarterPace * quarterDistance) },
        { name: 'Second Quarter', distance: `${quarterDistance} ${useKilometers ? 'km' : 'miles'}`, time: formatTime(secondQuarterPace * quarterDistance) },
        { name: 'Third Quarter', distance: `${quarterDistance} ${useKilometers ? 'km' : 'miles'}`, time: formatTime(thirdQuarterPace * quarterDistance) },
        { name: 'Fourth Quarter', distance: `${quarterDistance} ${useKilometers ? 'km' : 'miles'}`, time: formatTime(fourthQuarterPace * quarterDistance) }
      );
    }

    return sectors;
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.floor(minutes % 60);
    const seconds = Math.round((minutes % 1) * 60);
    return `${hours}:${remainingMinutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const calculateEquivalentTimes = (pace) => {
    if (!pace) return null;
    
    const [minutes, seconds] = pace.split(':')[0].split(':').map(Number);
    const paceInMinutes = minutes + (seconds / 60);
    
    const distances = [
      { name: '5km', distance: 5 },
      { name: '10km', distance: 10 },
      { name: 'Half Marathon', distance: 21.0975 },
      { name: 'Marathon', distance: 42.195 }
    ];
    
    return distances.map(({ name, distance }) => {
      const totalMinutes = paceInMinutes * distance;
      const hours = Math.floor(totalMinutes / 60);
      const remainingMinutes = Math.floor(totalMinutes % 60);
      const remainingSeconds = Math.round((totalMinutes % 1) * 60);
      
      return {
        name,
        time: `${hours}:${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
      };
    });
  };

  const calculateTrainingZones = (pace) => {
    if (!pace) return null;
    
    // Extract minutes and seconds from the pace string (e.g., "5:30 min/km")
    const [paceStr] = pace.split(' ');
    const [minutes, seconds] = paceStr.split(':').map(Number);
    const paceInMinutes = minutes + (seconds / 60);
    
    return [
      { 
        name: 'Recovery', 
        minPace: formatPace(paceInMinutes * 1.3),
        maxPace: formatPace(paceInMinutes * 1.2)
      },
      { 
        name: 'Easy', 
        minPace: formatPace(paceInMinutes * 1.2),
        maxPace: formatPace(paceInMinutes * 1.1)
      },
      { 
        name: 'Tempo', 
        minPace: formatPace(paceInMinutes * 1.1),
        maxPace: formatPace(paceInMinutes * 1.0)
      },
      { 
        name: 'Interval', 
        minPace: formatPace(paceInMinutes * 0.95),
        maxPace: formatPace(paceInMinutes * 0.85)
      }
    ];
  };

  // Detect light mode
  const isLightMode = document.body.classList.contains('light-mode') || document.querySelector('.App')?.classList.contains('light-mode');

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: isLightMode ? '#2d3559' : '#ffffff',
        },
      },
      title: {
        display: true,
        text: 'Pace Strategy Visualization',
        color: isLightMode ? '#2d3559' : '#ffffff',
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        min: 0,
        max: 10,
        title: {
          display: true,
          text: useKilometers ? 'Pace (min/km)' : 'Pace (min/mile)',
          color: isLightMode ? '#2d3559' : '#ffffff',
        },
        grid: {
          color: isLightMode ? 'rgba(45, 53, 89, 0.08)' : 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          color: isLightMode ? '#2d3559' : '#ffffff',
          stepSize: 1,
          callback: function(value) {
            const minutes = Math.floor(value);
            const seconds = Math.round((value - minutes) * 60);
            return `${minutes}:${seconds.toString().padStart(2, '0')}`;
          }
        }
      },
      x: {
        title: {
          display: true,
          text: useKilometers ? 'Distance (km)' : 'Distance (miles)',
          color: isLightMode ? '#2d3559' : '#ffffff',
        },
        grid: {
          color: isLightMode ? 'rgba(45, 53, 89, 0.08)' : 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          color: isLightMode ? '#2d3559' : '#ffffff'
        }
      }
    }
  };

  const calculateSensitivityTimes = (basePace, distance, useKilometers) => {
    if (!basePace || !distance) return null;
    
    // Extract pace from string like "5:30 min/km"
    const paceMatch = basePace.match(/(\d+):(\d+)/);
    if (!paceMatch) return null;
    
    const minutes = parseInt(paceMatch[1]);
    const seconds = parseInt(paceMatch[2]);
    let basePaceInSeconds = minutes * 60 + seconds;
    
    // Convert km pace to mile pace if using miles
    if (!useKilometers) {
      basePaceInSeconds = basePaceInSeconds * 1.60934; // Convert min/km to min/mile
    }
    
    const variations = [-10, -5, 0, 5, 10];
    const unit = useKilometers ? 'km' : 'mile';
    
    return variations.map(variation => {
      const newPaceInSeconds = basePaceInSeconds + variation;
      const totalSeconds = newPaceInSeconds * parseFloat(distance);
      
      const hours = Math.floor(totalSeconds / 3600);
      const remainingMinutes = Math.floor((totalSeconds % 3600) / 60);
      const remainingSeconds = Math.floor(totalSeconds % 60);
      
      // Format pace as MM:SS
      const paceMinutes = Math.floor(newPaceInSeconds / 60);
      const paceSeconds = Math.floor(newPaceInSeconds % 60);
      const formattedPace = `${paceMinutes}:${paceSeconds.toString().padStart(2, '0')}`;
      
      return {
        variation,
        pace: formattedPace,
        time: `${hours}:${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`,
        unit
      };
    });
  };

  const PaceSensitivity = ({ basePace, distance, useKilometers, totalTime }) => {
    const sensitivityData = calculateSensitivityTimes(basePace, distance, useKilometers);
    const unit = useKilometers ? 'km' : 'mile';
    const variations = [-10, -5, 0, 5, 10];
    
    return (
      <div className="pace-sensitivity">
        <h3>Pace Sensitivity</h3>
        <table>
          <thead>
            <tr>
              <th>Pace Variation</th>
              <th>New Pace</th>
              <th>Total Time</th>
            </tr>
          </thead>
          <tbody>
            {variations.map((variation, index) => (
              <tr key={index}>
                <td>{variation > 0 ? `+${variation}` : variation} sec/{unit}</td>
                <td>{sensitivityData ? `${sensitivityData[index].pace} (min/${unit})` : '-'}</td>
                <td className={variation === 0 ? 'highlight' : ''}>
                  {variation === 0 ? (!totalTime || totalTime === '00:00:00' || totalTime === ':00:00' ? '-' : totalTime) : (sensitivityData ? sensitivityData[index].time : '-')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  useEffect(() => {
    setChartData(generateChartData());
  }, [strategyMode, strategyPaces, distance, useKilometers]);

  return (
    <div className="pace-calculator">
      <div className="calculator-section">
        <div className="heading-container">
          <h2>Pace Calculator</h2>
        </div>
        <div className="mode-toggle">
          <button 
            className={`toggle-button ${isPaceMode ? 'active' : ''}`}
            onClick={() => toggleMode()}
            type="button"
          >
            Calculate Pace
          </button>
          <button 
            className={`toggle-button ${!isPaceMode ? 'active' : ''}`}
            onClick={() => toggleMode()}
            type="button"
          >
            Calculate Time
          </button>
        </div>
        <div className="unit-toggle">
          <button 
            className={`toggle-button ${!useKilometers ? 'active' : ''}`}
            onClick={() => toggleUnit()}
            type="button"
          >
            Miles
          </button>
          <button 
            className={`toggle-button ${useKilometers ? 'active' : ''}`}
            onClick={() => toggleUnit()}
            type="button"
          >
            Kilometers
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label htmlFor="race-distance">Race Distance:</label>
            <select
              id="race-distance"
              value={selectedDistance}
              onChange={handleDistanceChange}
              className="distance-select"
            >
              {raceDistances.map((race) => (
                <option key={race.value} value={race.value}>
                  {race.label}
                </option>
              ))}
            </select>
          </div>

          {selectedDistance === 'custom' && (
            <div className="input-group">
              <label htmlFor="distance">Custom Distance ({useKilometers ? 'km' : 'miles'}):</label>
              <input
                type="number"
                id="distance"
                value={customDistance}
                onChange={handleCustomDistanceChange}
                step="0.1"
                min="0.1"
                required
              />
            </div>
          )}

          {!isPaceMode && (
            <div className="strategy-toggle">
              <button 
                type="button"
                className={`toggle-button ${strategyMode === 'none' ? 'active' : ''}`}
                onClick={() => setStrategyMode('none')}
              >
                Single Pace
              </button>
              <button 
                type="button"
                className={`toggle-button ${strategyMode === 'halves' ? 'active' : ''}`}
                onClick={() => setStrategyMode('halves')}
              >
                Split in Halves
              </button>
              <button 
                type="button"
                className={`toggle-button ${strategyMode === 'quarters' ? 'active' : ''}`}
                onClick={() => setStrategyMode('quarters')}
              >
                Split in Quarters
              </button>
            </div>
          )}
          
          {isPaceMode ? (
            <div className="time-inputs">
              <div className="input-group">
                <label htmlFor="hours">Hours:</label>
                <input
                  type="number"
                  id="hours"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  min="0"
                />
              </div>
              
              <div className="input-group">
                <label htmlFor="minutes">Minutes:</label>
                <input
                  type="number"
                  id="minutes"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  min="0"
                  max="59"
                />
              </div>
              
              <div className="input-group">
                <label htmlFor="seconds">Seconds:</label>
                <input
                  type="number"
                  id="seconds"
                  value={seconds}
                  onChange={(e) => setSeconds(e.target.value)}
                  min="0"
                  max="59"
                />
              </div>
            </div>
          ) : strategyMode === 'none' ? (
            <div className="pace-inputs">
              <div className="input-group">
                <label htmlFor="pace-minutes">Minutes:</label>
                <input
                  type="number"
                  id="pace-minutes"
                  value={paceMinutes}
                  onChange={(e) => setPaceMinutes(e.target.value)}
                  min="0"
                  required
                />
              </div>
              
              <div className="input-group">
                <label htmlFor="pace-seconds">Seconds:</label>
                <input
                  type="number"
                  id="pace-seconds"
                  value={paceSeconds}
                  onChange={(e) => setPaceSeconds(e.target.value)}
                  min="0"
                  max="59"
                  required
                />
              </div>
              
              <div className="pace-unit">
                {useKilometers ? 'min/km' : 'min/mile'}
              </div>
            </div>
          ) : (
            renderStrategyInputs()
          )}

          <button type="submit">
            {isPaceMode ? 'Calculate Pace' : 'Calculate Time'}
          </button>
        </form>
      </div>

      <div className="result-section">
        {isPaceMode ? (
          <>
            <div className="result time-result">
              <h3>Your Pace:</h3>
              <p>{formatPace(pace, useKilometers)}</p>
            </div>
            <PaceSensitivity 
              basePace={pace} 
              distance={distance} 
              useKilometers={useKilometers} 
              totalTime={`${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`} 
            />
            {distance && <WorldRecords selectedDistance={distance} useKilometers={useKilometers} />}
          </>
        ) : (
          <div className="graph-section">
            <div className="time-result">
              <h3>Total Time</h3>
              <p>
                {totalTime || '-'}
              </p>
              {strategyMode !== 'none' && totalTime && (
                <p className="average-pace">
                  ({strategyMode === 'none' ? pace : calculateStrategyTime()?.averagePace} min/{useKilometers ? 'km' : 'mile'})
                </p>
              )}
            </div>
            <div className="pace-graph">
              <h3>Pace Strategy Visualization</h3>
              <div className="chart-container">
                <Line
                  data={chartData || {
                    labels: ['Start', 'Finish'],
                    datasets: [{
                      label: useKilometers ? 'Pace (min/km)' : 'Pace (min/mile)',
                      data: strategyMode === 'none' ? 
                        [0, 0] : 
                        ['Start', '25%', '50%', '75%', 'Finish'].slice(0, strategyMode === 'halves' ? 3 : 5),
                      borderColor: '#B100FF',
                      backgroundColor: strategyMode === 'none' ? 
                        ['#B100FF', '#B100FF'] : 
                        ['#B100FF', '#B100FF', '#FF9800', '#FF5722', '#F44336'].slice(0, strategyMode === 'halves' ? 3 : 5),
                      tension: 0.1,
                      pointRadius: 6,
                      pointHoverRadius: 8,
                    }]
                  }}
                  options={chartOptions}
                />
              </div>
            </div>
            {strategyMode !== 'none' && (
              <div className="sector-times">
                <h3>Sector Times</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Sector</th>
                      <th>Distance</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calculateSectorTimes()?.map((sector, index) => (
                      <tr key={index}>
                        <td>{sector.name}</td>
                        <td>{sector.distance}</td>
                        <td>{sector.time}</td>
                      </tr>
                    )) || (
                      <tr>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <PaceSensitivity 
              basePace={strategyMode === 'none' ? `${paceMinutes}:${paceSeconds}` : calculateStrategyTime()?.averagePace} 
              distance={distance} 
              useKilometers={useKilometers} 
              totalTime={totalTime} 
            />
            {distance && <WorldRecords selectedDistance={distance} useKilometers={useKilometers} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaceCalculator; 